import apiClient from '../client';
import { ApiUrls } from '../urls';
import { fetchWithAuth } from '../helpers/fetchWithHeaders';
import type {
  ConversationHeaders,
  FirstChunkHeaders,
  StreamingConversationParams,
  ConversationDetails,
} from '../../types/conversation.types';

export interface ChatRequest {
  message: string;
  mode?: 'uncover' | 'analyze' | 'design' | 'test';
  session_id?: string;
  tmp_conversation_id?: string;
  conversation_id?: string;
  project_id?: string;
  attachment_ids?: string[];
  skip_analysis?: boolean;
}

export interface ChatResponse {
  response: string;
  conversation_id?: string;
  [key: string]: any;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Extract conversation headers from HTTP response
 */
const extractConversationHeaders = (response: Response): ConversationHeaders => {
  const getHeader = (name: string): string | null => {
    return response.headers.get(name) || response.headers.get(name.toLowerCase());
  };

  const reflection = getHeader('X-Reflection') === 'true';
  const clarification = getHeader('X-Clarification') === 'true';
  const repeated = getHeader('X-Repeated') === 'true';
  const prototyper = getHeader('X-Prototyper') === 'true';
  const canBeDrawn = getHeader('X-Design-Type');
  const readyToGenerate = getHeader('X-Ready-To-Generate');
  const isReadyToCreate = getHeader('X-Ready-To-Create') === 'true' || getHeader('X-Ready-To-Create') === '1';
  const isReadyToCreateAccount = getHeader('X-Ready-To-Create-Account') === 'true' || getHeader('X-Ready-To-Create-Account') === '1';
  const detectedLanguageHeader = getHeader('X-Detected-Language');
  const detectedLanguage: 'rtl' | 'ltr' | undefined =
    detectedLanguageHeader === 'rtl' || detectedLanguageHeader === 'ltr' ? detectedLanguageHeader : undefined;

  return {
    reflection: reflection || false,
    clarification: clarification || false,
    repeated: repeated || false,
    prototyper: prototyper || false,
    canBeDrawn: (canBeDrawn && canBeDrawn !== 'null' && canBeDrawn !== 'none' && canBeDrawn !== 'None') ? canBeDrawn : undefined,
    readyToGenerate: (readyToGenerate && readyToGenerate !== 'null' && readyToGenerate !== 'none' && readyToGenerate !== 'None') ? readyToGenerate : undefined,
    isReadyToCreate: isReadyToCreate || false,
    isReadyToCreateAccount: isReadyToCreateAccount || false,
    detectedLanguage,
  };
};

/**
 * Build a rich HTTP error with status and message from response body
 */
const buildHttpError = async (response: Response): Promise<Error & { status?: number }> => {
  const defaultMessage = `HTTP error! status: ${response.status}`;
  let message = defaultMessage;

  try {
    const bodyText = await response.text();
    if (bodyText) {
      try {
        const data = JSON.parse(bodyText);
        message = data?.error || data?.message || defaultMessage;
      } catch {
        message = bodyText;
      }
    }
  } catch {
    // Ignore parsing errors and fall back to default message
  }

  const error = new Error(message) as Error & { status?: number };
  error.status = response.status;
  return error;
};

/**
 * Determine the correct API URL based on request parameters
 */
const getConversationURL = (request: StreamingConversationParams): string => {
  if (request.conversation_id && request.project_id) {
    return ApiUrls.conversations(request.conversation_id);
  } else if (request.conversation_id) {
    return ApiUrls.tmpConversationsInteractions(request.conversation_id);
  } else if (request.project_id) {
    return ApiUrls.projectConversations(request.project_id);
  }
  return ApiUrls.tmpConversations;
};

/**
 * Clean request payload - remove URL parameters that should not be in body
 */
const cleanRequestPayload = (request: StreamingConversationParams): StreamingConversationParams => {
  return {
    ...request,
    session_id: request.project_id || request.conversation_id ? undefined : request.session_id,
    project_id: undefined, // Always remove from body (it's in URL if needed)
    conversation_id: undefined, // Always remove from body (it's in URL if needed)
  };
};

// ============================================================================
// STREAMING CONVERSATION SERVICE
// ============================================================================

/**
 * Create a streaming conversation
 * Handles Server-Sent Events (SSE) or chunked transfer encoding
 */
export const createConversationStream = async (
  request: StreamingConversationParams,
  onChunk: (chunk: string, firstChunkHeaders?: FirstChunkHeaders, allHeaders?: ConversationHeaders) => void,
  onError: (error: Error) => void,
  onComplete: (conversationId: string, headers: ConversationHeaders) => void,
  signal?: AbortSignal
): Promise<void> => {
  try {
    const response = await fetchWithAuth(getConversationURL(request), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream, text/plain, application/json',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify(cleanRequestPayload(request)),
      signal,
    });

    if (!response.ok) {
      throw await buildHttpError(response);
    }

    // Extract conversation ID, LLM interaction ID, and headers
    const conversationId = response.headers.get('X-Conversation-ID') ||
      response.headers.get('conversation-id') || '';
    const llmInteractionId = response.headers.get('X-LLM-Interaction-ID') ||
      response.headers.get('x-llm-interaction-id') || undefined;
    const headers = extractConversationHeaders(response);

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let isFirstChunk = true;

    try {
      let hasReceivedData = false;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      const readWithTimeout = () => {
        return Promise.race([
          reader.read(),
          new Promise<{ done: boolean; value?: Uint8Array }>((_, reject) => {
            timeoutId = setTimeout(() => {
              reject(new Error('Stream read timeout'));
            }, 300000); // 5 minutes timeout
          })
        ]);
      };

      while (true) {
        try {
          const { done, value } = await readWithTimeout();
          if (timeoutId) clearTimeout(timeoutId);

          if (done) {
            onComplete(conversationId, headers);
            break;
          }

          if (!hasReceivedData) {
            hasReceivedData = true;
          }

          let chunk = decoder.decode(value, { stream: true });

          // Handle Server-Sent Events format
          if (chunk.includes('data: ')) {
            const lines = chunk.split('\n');
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.substring(6).trim();
                if (data && data !== '[DONE]') {
                  if (isFirstChunk) {
                    onChunk(data, {
                      conversationId,
                      llmInteractionId,
                      headers: {
                        detectedLanguage: headers.detectedLanguage,
                      },
                    }, headers);
                    isFirstChunk = false;
                  } else {
                    onChunk(data);
                  }
                }
              }
            }
          } else {
            // Handle plain chunked text
            if (isFirstChunk) {
              onChunk(chunk, {
                conversationId,
                llmInteractionId,
                headers: {
                  detectedLanguage: headers.detectedLanguage,
                },
              }, headers);
              isFirstChunk = false;
            } else {
              onChunk(chunk);
            }
          }
        } catch (error) {
          if (error instanceof Error && error.message.includes('timeout')) {
            console.warn('Stream timeout, attempting fallback...');
            // Fallback to non-streaming approach
            await createConversationFallback(request, onChunk, onError, onComplete, signal);
            return;
          }
          throw error;
        }
      }
    } finally {
      reader.releaseLock();
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.log('Stream aborted by user');
      return;
    }
    onError(error instanceof Error ? error : new Error('Unknown error occurred'));
  }
};

/**
 * Fallback to non-streaming conversation creation
 * Used when streaming is not supported or fails
 */
export const createConversationFallback = async (
  request: StreamingConversationParams,
  onChunk: (chunk: string, firstChunkHeaders?: FirstChunkHeaders, allHeaders?: ConversationHeaders) => void,
  onError: (error: Error) => void,
  onComplete: (conversationId: string, headers: ConversationHeaders) => void,
  signal?: AbortSignal
): Promise<void> => {
  try {
    const response = await fetchWithAuth(getConversationURL(request), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(cleanRequestPayload(request)),
      signal,
    });

    if (!response.ok) {
      throw await buildHttpError(response);
    }

    const conversationId = response.headers.get('X-Conversation-ID') ||
      response.headers.get('conversation-id') || '';
    const headers = extractConversationHeaders(response);
    const fullResponse = await response.text();

    // Simulate streaming by sending chunks
    const words = fullResponse.split(' ');
    for (let i = 0; i < words.length; i++) {
      const chunk = i === 0 ? words[i] : ' ' + words[i];
      if (i === 0) {
        onChunk(chunk, {
          conversationId,
          headers: {
            detectedLanguage: headers.detectedLanguage,
          },
        }, headers);
      } else {
        onChunk(chunk);
      }
      // Small delay to simulate streaming
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    onComplete(conversationId, headers);
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return;
    }
    onError(error instanceof Error ? error : new Error('Unknown error occurred'));
  }
};

// ============================================================================
// CONVERSATION HISTORY & UTILITY FUNCTIONS
// ============================================================================

/**
 * Load conversation history from API
 * Converts API format to message format
 */
export const loadConversationHistory = async (
  conversationId: string,
  projectId?: string | null
): Promise<any[]> => {
  try {
    const url = projectId
      ? ApiUrls.conversations(conversationId)
      : ApiUrls.tmpConversationsLLM(conversationId);

    const response = await apiClient.get(url);
    const interactions = response.data;

    // Sort by interactionOrder to maintain conversation flow
    const sortedInteractions = interactions.sort((a: any, b: any) =>
      a.interactionOrder - b.interactionOrder
    );

    // Convert API format to conversation messages format
    const messages: any[] = [];

    sortedInteractions.forEach((interaction: any) => {
      // Add user message (include messages with only attachments)
      if ((interaction.userMessage || (interaction.attachments_ids && interaction.attachments_ids.length > 0))
        && !interaction.isHiddenUserQuery) {
        messages.push({
          id: interaction.id + '_user', // Ensure string ID
          content: interaction.userMessage || '',
          attachments_ids: interaction.attachments_ids,
          role: 'user',
        });
      }

      // Add assistant message
      if (interaction.llmReply) {
        messages.push({
          id: interaction.id,
          content: interaction.llmReply,
          role: 'assistant',
          actionType: interaction.actionType,
          nodeType: interaction.nodeType,
          readyToGenerate: interaction.readyToGenerate || null,
          mode: interaction.mode,
          detectedLanguage: interaction.detectedLanguage,
          llmInteractionId: interaction.id, // Store the real backend UUID for feedback
          isLiked: interaction.isLiked, // Store feedback state from backend
          prototyper_demo_link_id: interaction.prototyper_demo_link_id || null,
        });
      }
    });

    return messages;
  } catch (error) {
    console.error('Failed to load conversation history:', error);
    throw error;
  }
};

/**
 * Clean user actions (e.g., openConversationAndDrawing)
 */
export const cleanUserActions = async (actionType: string): Promise<void> => {
  try {
    await apiClient.post(ApiUrls.cleanUserActions, { action_type: actionType });
  } catch (error) {
    console.error('Failed to clean user actions:', error);
    throw error;
  }
};

/**
 * Handle FigJam plugin action - add user action for opening in FigJam
 */
export const handleFigJamPluginAction = async (
  conversationId: string,
  conversationName: string,
  generateType: string | null,
  projectId?: string | null,
  hiddenMessage?: string | null
): Promise<void> => {
  try {
    const actionData = {
      type: 'openConversationAndDrawing',
      conversationID: conversationId,
      conversationName,
      generateType,
      projectId,
      ...(hiddenMessage && { hiddenMessage }),

    };

    await apiClient.post(ApiUrls.addUserAction, actionData);
  } catch (error) {
    console.error('Failed to handle FigJam plugin action:', error);
    throw error;
  }
};

class ConversationService {
  /**
   * Send a chat message to the AI
   */
  async sendChatMessage(request: ChatRequest): Promise<ChatResponse> {
    try {
      const response = await apiClient.post<ChatResponse>(ApiUrls.chat, request);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get conversation history by conversation ID
   */
  async getConversationHistory(conversationId: string): Promise<any> {
    try {
      const response = await apiClient.get(ApiUrls.conversations(conversationId));
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get temporary conversation interactions
   */
  async getTmpConversationInteractions(conversationId: string): Promise<any> {
    try {
      const response = await apiClient.get(ApiUrls.tmpConversationsInteractions(conversationId));
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Create a temporary conversation
   */
  async createTmpConversation(data: {
    name: string;
    mode?: string;
    init_message?: string;
  }): Promise<any> {
    try {
      const response = await apiClient.post(ApiUrls.tmpConversations, data);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get conversation by ID
   */
  async getConversationById(conversationId: string): Promise<any> {
    try {
      const response = await apiClient.get(ApiUrls.conversationsById(conversationId));
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Save drawing data to temporary conversation
   */
  async saveTmpConversationDrawing(conversationId: string, drawingData: any): Promise<any> {
    try {
      const response = await apiClient.post(
        ApiUrls.tmpConversationDrawing(conversationId),
        drawingData
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Save drawing data to project conversation
   */
  async saveProjectConversationDrawing(
    projectId: string,
    conversationId: string,
    drawingData: any
  ): Promise<any> {
    try {
      const response = await apiClient.post(
        ApiUrls.projectConversationDrawing(projectId, conversationId),
        drawingData
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get tokens for a conversation
   */
  async getTokens(interactionId: string): Promise<any> {
    try {
      const response = await apiClient.get(ApiUrls.tokens(interactionId));
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get request cost
   */
  async getRequestCost(requestId: string): Promise<any> {
    try {
      const response = await apiClient.get(ApiUrls.requestCost(requestId));
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Send like/dislike feedback for an LLM interaction.
   * Accepts either a llmInteractionId (for persisted conversation interactions)
   * or a tmpLlmInteractionId (for temporary interactions). Body: { isLiked: boolean }
   */
  async sendLlmInteractionFeedback(params: { messageID?: string; isLiked: boolean; projectId?: string; }): Promise<any> {
    try {
      const { messageID, isLiked, projectId } = params;

      if (projectId) {
        if (messageID) {
          const url = ApiUrls.llmInteractionLike(messageID);
          const response = await apiClient.patch(url, { isLiked });
          return response.data;
        }

        throw new Error('projectId provided but no interaction id (llmInteractionId or tmpLlmInteractionId) was given');
      }
      if (!projectId) {

        if (messageID) {
          const url = ApiUrls.tmpLlmInteractionLike(messageID);
          const response = await apiClient.patch(url, { isLiked });
          return response.data;
        }

        throw new Error('projectId Not provided');
      }
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors consistently
   */
  private handleError(error: any): Error {
    if (error.response) {
      const message = error.response.data?.message || 'Request failed';
      return new Error(message);
    }

    // Network error
    return new Error('Network error. Please check your connection.');
  }
}

export const conversationService = new ConversationService();
export default conversationService;

